"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import debounce from "lodash.debounce";
import type {
    LomdaContent,
    LomdaMetadata,
    MediaAsset,
    UpdateLomdaData,
} from "@/features/lomdaEditorV2/lib/lomdaTypes";
import { useUpdateLomdaV2 } from "../api/useUpdateLomdaV2";

/** Raw payload — references compared by identity to detect "changed since last save". */
export interface AutoSaveRawData {
    metadata: LomdaMetadata;
    content: LomdaContent;
    name: string;
    assets: MediaAsset[];
}

export interface UseAutoSaveProps {
    lomdaId: string;
}

const AUTO_SAVE_DELAY = 2000;

export const useAutoSave = ({ lomdaId }: UseAutoSaveProps) => {
    const { mutate, isPending, isError, error } = useUpdateLomdaV2(lomdaId);
    const [lastSaved, setLastSaved] = useState<Date | null>(null);
    const lastSavedRawRef = useRef<AutoSaveRawData | null>(null);
    const currentRawRef = useRef<AutoSaveRawData | null>(null);
    const debouncedRef = useRef<ReturnType<typeof debounce> | null>(null);

    /** Reference equality is sufficient: React state preserves identity until a real
     *  update occurs, so a different reference here always means a real change. */
    const hasDataChanged = useCallback((next: AutoSaveRawData): boolean => {
        const last = lastSavedRawRef.current;
        if (!last) return true;
        if (next.metadata !== last.metadata) return true;
        if (next.content !== last.content) return true;
        if (next.name !== last.name) return true;
        if (next.assets !== last.assets) return true;
        return false;
    }, []);

    useEffect(() => {
        debouncedRef.current = debounce(() => {
            const raw = currentRawRef.current;
            if (!raw || !hasDataChanged(raw)) return;
            // Serialize at save time, not on every keystroke. This is the only
            // place that pays the JSON.stringify cost for content/metadata/assets.
            const serializedAssets = raw.assets.map((a) => ({
                ...a,
                createdAt: a.createdAt.toISOString(),
            }));
            const payload: UpdateLomdaData = {
                name: raw.name,
                metadata: JSON.stringify(raw.metadata),
                content: JSON.stringify(raw.content),
                assets: JSON.stringify(serializedAssets),
            };
            mutate(payload, {
                onSuccess: () => {
                    lastSavedRawRef.current = raw;
                    setLastSaved(new Date());
                },
            });
        }, AUTO_SAVE_DELAY);

        // Flush (not cancel) on teardown so a pending change isn't lost when the
        // editor unmounts (route change / navigation).
        return () => {
            debouncedRef.current?.flush();
        };
    }, [mutate, hasDataChanged]);

    // Don't lose the last ≤2s of edits when the tab is hidden or closed. Flush
    // the pending save on hide, and warn before unload while changes are still
    // unsaved.
    useEffect(() => {
        const flush = () => debouncedRef.current?.flush();
        const onVisibility = () => {
            if (document.visibilityState === "hidden") flush();
        };
        const onBeforeUnload = (e: BeforeUnloadEvent) => {
            const raw = currentRawRef.current;
            if (raw && hasDataChanged(raw)) {
                flush();
                e.preventDefault();
                e.returnValue = "";
            }
        };
        window.addEventListener("pagehide", flush);
        document.addEventListener("visibilitychange", onVisibility);
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => {
            window.removeEventListener("pagehide", flush);
            document.removeEventListener("visibilitychange", onVisibility);
            window.removeEventListener("beforeunload", onBeforeUnload);
        };
    }, [hasDataChanged]);

    const setLomdaData = useCallback((data: AutoSaveRawData) => {
        currentRawRef.current = data;
        // First call after mount: treat this payload as the saved baseline so
        // the next debounced save sees no diff and skips. Without this the
        // editor always saves once on load because lastSavedRawRef starts null
        // and hasDataChanged then returns true for any incoming payload.
        if (lastSavedRawRef.current === null) {
            lastSavedRawRef.current = data;
        }
    }, []);

    const saveLomda = useCallback(() => {
        if (debouncedRef.current) debouncedRef.current();
    }, []);

    return {
        setLomdaData,
        saveLomda,
        isSaving: isPending,
        saveError: isError ? (error?.message ?? "Save failed") : null,
        lastSaved,
    };
};
